import React, { FC, useRef } from 'react';
import { Animated } from 'react-native';

import { menuTestIDs, onDemandPageTestIDs } from '@constants';
import { useAppContextNodeHandle, useAppContextReference, useContextNodeHandle, useContextReference } from '@hooks';
import { VodLadingState } from '@store';
import { ContinueWatchingResponse } from '@types';

import { continueWatchingCategoryData, LAYOUT_OFFSET } from './constants';

import { Headroom } from '../../../components/headroom/Headroom';
import { OnDemandPagePromoSection } from '../../../components/onDemand/components/OnDemandPagePromo';
import { OnDemandPageLayout } from '../../../components/onDemand/layout/OnDemandPageLayout/OnDemandPageLayout';

type OnDemandContentProps = {
  data?: VodLadingState['homepage'];
  continueWatching?: ContinueWatchingResponse;
};

export const OnDemandContent: FC<OnDemandContentProps> = ({ data, continueWatching }) => {
  const promosArray: VodLadingState['homepage'] = [];
  const listLayoutData: VodLadingState['homepage'] = [];

  // Separate out promo content from vod rows
  data?.forEach(row => {
    const isPromo = row.category.custom_fields.some(field => field.field_value === 'promo');

    if (isPromo) {
      promosArray.push(row);
    } else {
      listLayoutData.push(row);
    }
  });

  const continueWatchingData = continueWatching?.data?.['continue-watching'];

  const allLayoutData =
    continueWatchingData && continueWatchingData?.length > 0
      ? [
          ...listLayoutData,
          {
            category: continueWatchingCategoryData,
            channels: continueWatchingData,
          },
        ]
      : listLayoutData;

  const hidePromo = !promosArray || promosArray.length === 0;

  const layoutOffset = useRef(new Animated.Value(hidePromo ? 0 : LAYOUT_OFFSET)).current;

  const promoReference = useContextReference(onDemandPageTestIDs.promoArea);
  const layoutHandle = useContextNodeHandle(onDemandPageTestIDs.mainLayout);
  const menuHandle = useAppContextNodeHandle(menuTestIDs.mainMenu);
  const mainMenuReference = useAppContextReference(menuTestIDs.mainMenu);

  const layoutValue = hidePromo ? 0 : LAYOUT_OFFSET;

  const headerComponent = hidePromo ? null : (
    <OnDemandPagePromoSection
      continueWatching={continueWatching?.data['continue-watching'] || []}
      nextFocusDown={layoutHandle}
      nextFocusUp={menuHandle}
      promos={promosArray}
      testID={onDemandPageTestIDs.promoArea}
    />
  );

  const layoutComponent = (
    <OnDemandPageLayout
      data={allLayoutData}
      // Focusable flatlist has refs here, not sure why
      // nextFocusUp works with references not with handles
      nextFocusUp={hidePromo ? mainMenuReference : promoReference}
      testID={hidePromo ? onDemandPageTestIDs.promoArea : onDemandPageTestIDs.mainLayout}
    />
  );

  return (
    <Headroom
      headerComponent={headerComponent}
      headerTestID="promoAnimatedWrapper"
      layoutComponent={layoutComponent}
      layoutOffset={layoutOffset}
      layoutValue={layoutValue}
    />
  );
};
